<template>
    <v-app>
        <v-main class="grey lighten-3">
            <v-container class="py-6" style="max-width: 900px">
                <v-card :loading="loading" class="pa-2">
                    <!-- Loading -->
                    <v-card-text v-if="loading" class="text-center py-10">
                        <v-progress-circular indeterminate color="primary" />
                        <p class="mt-4 mb-0">{{ labels.loading }}</p>
                    </v-card-text>

                    <!-- Error -->
                    <v-card-text
                        v-else-if="errorMessage"
                        class="text-center py-10"
                    >
                        <v-icon size="56" color="error"
                            >mdi-alert-circle-outline</v-icon
                        >
                        <p class="mt-4 mb-0 text-body-1">{{ errorMessage }}</p>
                    </v-card-text>

                    <!-- Already signed / submitted -->
                    <v-card-text
                        v-else-if="alreadySigned || submitted"
                        class="text-center py-10"
                    >
                        <v-icon size="56" color="success"
                            >mdi-check-circle-outline</v-icon
                        >
                        <p class="mt-4 mb-0 text-body-1">
                            {{
                                submitted ? labels.thanks : labels.alreadySigned
                            }}
                        </p>
                    </v-card-text>

                    <!-- Certificate preview -->
                    <div
                        v-else
                        class="certificate-preview"
                        v-html="certificateHtml"
                        @click="onCertificateClick"
                    />
                </v-card>
            </v-container>

            <v-dialog
                v-model="signDialog"
                persistent
                max-width="560px"
                :retain-focus="false"
            >
                <v-card :loading="submitting">
                    <v-card-title class="text-h6">
                        {{ labels.signTitle }}
                    </v-card-title>
                    <v-card-text>
                        <v-form ref="form" v-model="valid" lazy-validation>
                            <SignatureBlock
                                v-model="signer"
                                :label="labels.supplierSignature"
                                :language="language"
                                hide-title
                            />
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text @click="signDialog = false">
                            {{ labels.cancel }}
                        </v-btn>
                        <v-btn
                            color="primary"
                            rounded
                            elevation="0"
                            :loading="submitting"
                            :disabled="!canSubmit"
                            @click="submit"
                        >
                            {{ labels.submitSignature }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-main>
    </v-app>
</template>

<script>
import API from '@/services/api'
import { auth } from '@/services/firebase'
import SignatureBlock from './SignatureBlock.vue'
import { saveFile } from '@/services/storage/saveFile.js'
import {
    buildReceiptCertificate,
    getReceiptCertificatePdfFile,
} from './receiptCertificate.js'
import { isSpanishCertificateLanguage } from './receiptCertificateHelpers.js'

const emptySigner = () => ({
    name: '',
    documentId: '',
    signatureMode: 'draw',
    signatureData: null,
    signaturePreview: null,
    signatureFile: null,
})

export default {
    name: 'ReceptionCertificateSignature',
    components: {
        SignatureBlock,
    },
    data() {
        return {
            loading: true,
            submitting: false,
            submitted: false,
            valid: true,
            errorMessage: '',
            certificateHtml: '',
            alreadySigned: false,
            companyId: '',
            purchaseId: '',
            companyLogo: '',
            receptionEntry: null,
            language: 'Spanish',
            signDialog: false,
            signer: emptySigner(),
        }
    },
    computed: {
        token() {
            return this.$router.currentRoute.query.text
        },
        isSpanish() {
            return isSpanishCertificateLanguage(this.language)
        },
        labels() {
            if (this.isSpanish) {
                return {
                    loading: 'Cargando información...',
                    thanks: 'Gracias. Su firma fue registrada correctamente.',
                    alreadySigned:
                        'Este documento ya fue firmado y el enlace ya no está disponible.',
                    signTitle: 'Firmar Acta de Recibo',
                    supplierSignature: 'Firma del proveedor',
                    cancel: 'Cancelar',
                    submitSignature: 'Enviar firma',
                    invalidLink:
                        'El enlace no es válido. Por favor solicite uno nuevo.',
                    authError:
                        'No fue posible autenticar la sesión. Intente nuevamente.',
                    loadError: 'No fue posible cargar la información del acta.',
                    signError: 'No fue posible registrar la firma.',
                }
            }
            return {
                loading: 'Loading information...',
                thanks:
                    'Thank you. Your signature was registered successfully.',
                alreadySigned:
                    'This document has already been signed and the link is no longer available.',
                signTitle: 'Sign Acceptance Certificate',
                supplierSignature: 'Supplier signature',
                cancel: 'Cancel',
                submitSignature: 'Submit signature',
                invalidLink: 'The link is not valid. Please request a new one.',
                authError:
                    'Could not authenticate the session. Please try again.',
                loadError: 'Could not load the certificate information.',
                signError: 'Could not register the signature.',
            }
        },
        canSubmit() {
            return (
                !!this.signer.name &&
                !!this.signer.documentId &&
                !!this.signer.signatureData
            )
        },
    },
    created() {
        if (!this.token) {
            this.errorMessage = this.labels.invalidLink
            this.loading = false
            return
        }
        auth().onAuthStateChanged(user => {
            if (!user) {
                auth()
                    .signInAnonymously()
                    .catch(() => {
                        this.errorMessage = this.labels.authError
                        this.loading = false
                    })
            } else {
                this.loadCertificate()
            }
        })
    },
    methods: {
        onCertificateClick(event) {
            const target = event.target
            if (
                target &&
                (target.id === 'sign-supplier-btn' ||
                    (target.closest && target.closest('#sign-supplier-btn')))
            ) {
                this.signDialog = true
            }
        },
        async renderCertificate(showSignButton) {
            const { htmlContent } = await buildReceiptCertificate({
                receptionEntry: this.receptionEntry,
                companyId: this.companyId,
                purchaseId: this.purchaseId,
                companyLogo: this.companyLogo,
                showSupplierSignButton: showSignButton,
                language: this.language,
            })
            const parser = new DOMParser()
            const doc = parser.parseFromString(htmlContent, 'text/html')
            const styles = [...doc.querySelectorAll('style')]
                .map(style => style.outerHTML)
                .join('')
            this.certificateHtml = `${styles}${doc.body.innerHTML}`
        },
        async loadCertificate() {
            try {
                this.loading = true
                const data = await API.getReceptionCertificateForSignature(
                    this.token
                )
                this.companyId = data.companyId
                this.purchaseId = data.purchaseId
                this.companyLogo = data.companyLogo || ''
                this.receptionEntry = data.receptionEntry
                this.language =
                    data.language ||
                    data.receptionEntry?.supplierLanguage ||
                    'Spanish'
                this.alreadySigned = !!data.alreadySigned
                if (!this.alreadySigned) {
                    await this.renderCertificate(true)
                }
            } catch (error) {
                this.errorMessage = error.message || this.labels.loadError
            } finally {
                this.loading = false
            }
        },
        async submit() {
            if (this.$refs.form && !this.$refs.form.validate()) return
            if (!this.canSubmit) return
            try {
                this.submitting = true
                await API.signReceptionCertificate({
                    params: this.token,
                    name: this.signer.name,
                    documentId: this.signer.documentId,
                    signatureBase64: this.signer.signatureData,
                })

                this.receptionEntry = {
                    ...this.receptionEntry,
                    supplierSignature: {
                        name: this.signer.name,
                        documentId: this.signer.documentId,
                        signatureFile: 'supplier-signature.png',
                        signedOn: new Date(),
                    },
                }

                try {
                    await new Promise(resolve => setTimeout(resolve, 400))
                    const {
                        file,
                        fileName,
                    } = await getReceiptCertificatePdfFile({
                        receptionEntry: this.receptionEntry,
                        companyId: this.companyId,
                        purchaseId: this.purchaseId,
                        companyLogo: this.companyLogo,
                        language: this.language,
                    })
                    const storageFileName = 'certificate-signed.pdf'
                    const storagePath = `${this.companyId}/purchases/${this.purchaseId}/receptionEntries/${this.receptionEntry.id}`
                    await saveFile(file, storagePath, storageFileName)
                    await API.sendReceptionCertificateSignatureConfirmation({
                        params: this.token,
                        pdfStoragePath: `${storagePath}/${storageFileName}`,
                        pdfFileName: fileName,
                    })
                } catch (_) {
                    // Signature already saved; confirmation email is best-effort
                }

                this.signDialog = false
                this.submitted = true
            } catch (error) {
                this.errorMessage = error.message || this.labels.signError
                this.signDialog = false
            } finally {
                this.submitting = false
            }
        },
    },
}
</script>

<style scoped>
.certificate-preview {
    background: #fff;
    overflow-x: auto;
}
.certificate-preview >>> body {
    padding: 16px !important;
}
.certificate-preview >>> .content {
    max-width: 100% !important;
}
</style>
